<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { ref, onMounted } from "vue";
import Loading from 'vue-loading-overlay';
import { useForm } from '@inertiajs/vue3';
import { useToast } from 'vue-toast-notification';
import Pagination from '@/Components/Pagination.vue';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

const props = defineProps({
    year: String,
    flash: Object,
    permissions: Object,
    user: Object,
});
let isLoading = ref(false);
const $toast = useToast();
dayjs.extend(advancedFormat);

const PermissionAddform = useForm({
    permissionName: '',
});

const PermissionAddformSubmit = () => {
    PermissionAddform.post('/permissions/store', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => {
            isLoading.value = false;
            document.querySelector('#addPermissionModal').classList.remove('show');
            document.querySelector('.modal-backdrop').classList.remove('show');
        },
    });
};

onMounted(() => {
    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

const getInitials = (name) => {
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    let initials = [...name.matchAll(rgx)] || [];
    initials = (
        (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
    return initials;
};
</script>
<style scoped>
@import "vue-loading-overlay/dist/vue-loading.css";
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
</style>
<template>
    <AppLayout title="Permissions Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="alert alert-primary alert-dismissible" role="alert" v-if="$page.props.flash.message">
                <h6 class="alert-heading d-flex align-items-center fw-bold mb-1">Message</h6>
                <p class="mb-0">{{ $page.props.flash.message }}</p>
                <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
            </div>

            <h4 class="fw-bold py-3 mb-2">Permissions List</h4>
            <p class="mb-4">
                Each category (Basic, Professional, and Business) includes the four predefined roles shown below.
            </p>

            <!-- Permission Table -->
            <div class="card">
                <div class="card-datatable table-responsive">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="row mx-1">
                            <div class="col-sm-12 col-md-3">
                                <div class="dataTables_length" id="DataTables_Table_0_length">
                                    <label>

                                    </label>
                                </div>
                            </div>
                            <div class="col-sm-12 col-md-9">
                                <div
                                    class="dt-action-buttons text-xl-end text-lg-start text-md-end text-start d-flex align-items-center justify-content-md-end justify-content-center flex-wrap me-1">
                                    <div class="me-3">
                                        <div id="DataTables_Table_0_filter" class="dataTables_filter">
                                            <label>
                                            </label>
                                        </div>
                                    </div>
                                    <div class="dt-buttons"
                                        v-if="$page.props.user.permissions.includes('add-permissions')">
                                        <button class="dt-button add-new btn btn-primary mb-3 mb-md-0" tabindex="0"
                                            aria-controls="DataTables_Table_0" type="button" data-bs-toggle="modal"
                                            data-bs-target="#addPermissionModal"><span>Add Permission</span></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <table class="datatables-permissions table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1391px;">
                            <thead>
                                <tr role="row">
                                    <th class="control sorting_disabled" rowspan="1" colspan="1"
                                        style="width: 0px; display: none;" aria-label=""></th>
                                    <th class="sorting" tabindex="0" aria-controls="DataTables_Table_0" rowspan="1"
                                        colspan="1" style="width: 302px;"
                                        aria-label="Name: activate to sort column ascending">Name</th>
                                    <th class="sorting_disabled" rowspan="1" colspan="1" style="width: 479px;"
                                        aria-label="Assigned To">Assigned To</th>
                                    <th class="sorting_disabled" rowspan="1" colspan="1" style="width: 297px;"
                                        aria-label="Created Date">Created Date</th>
                                    <th class="sorting_disabled" rowspan="1" colspan="1" style="width: 147px;"
                                        aria-label="Actions">
                                        Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="permission in props.permissions.data" :key="permission.id">
                                    <td class=" control" tabindex="0" style="display: none;"></td>
                                    <td><span class="text-nowrap">{{ permission.name }}</span></td>
                                    <td><span class="text-nowrap"><a href="app-user-list.html"><span
                                                    class="badge  bg-label-primary m-1">Administrator</span></a></span>
                                    </td>
                                    <td><span class="text-nowrap">
                                            {{
                                                dayjs(permission.created_at).format('Do MMMM YYYY, HH:mm')
                                            }}
                                        </span>
                                    </td>
                                    <td><span class="text-nowrap"><button class="btn btn-sm btn-icon me-2"
                                                data-bs-target="#editPermissionModal" data-bs-toggle="modal"
                                                data-bs-dismiss="modal"><i class="bx bx-edit"></i></button><button
                                                class="btn btn-sm btn-icon delete-record"><i
                                                    class="bx bx-trash"></i></button></span></td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="row mx-2">
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_info" id="DataTables_Table_0_info" role="status"
                                    aria-live="polite">Showing {{
                                        permissions.from
                                    }} to {{ permissions.to }} of {{
    permissions.total
}} Packages</div>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_paginate">
                                    <Pagination :links="permissions.links"></Pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!--/ Permission Table -->

            <!-- Modal -->
            <!-- Add Permission Modal -->
            <div class="modal fade" id="addPermissionModal" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered modal-simple">
                    <div class="modal-content p-3 p-md-5">
                        <div class="modal-body">
                            <button type="button" class="btn-close btn-pinned" data-bs-dismiss="modal"
                                aria-label="Close"></button>
                            <div class="text-center mb-4">
                                <h3>Add New Permission</h3>
                                <p>Permissions you may use and assign to your users.</p>
                            </div>
                            <form @submit.prevent="PermissionAddformSubmit">
                                <div class="col-12 mb-3">
                                    <label class="form-label" for="modalPermissionName">Permission Name</label>
                                    <input type="text" id="permissionName" v-model="PermissionAddform.permissionName"
                                        name="permissionName" class="form-control" placeholder="Permission Name"
                                        autofocus />
                                </div>

                                <div class="col-12 text-center demo-vertical-spacing">
                                    <button type="submit" class="btn btn-primary me-sm-3 me-1">Create
                                        Permission</button>
                                    <button type="reset" class="btn btn-label-secondary" data-bs-dismiss="modal"
                                        aria-label="Close">
                                        Discard
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <!--/ Add Permission Modal -->
        </div>
    </AppLayout>
</template>